import React, { useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { Button } from "../../ui/button";
import { VendorSpend } from "./observability/VendorSpend";
import { SpendChart } from "./observability/SpendChart";
import { UsageBreakdowns } from "./observability/UsageBreakdowns";
import { FounderInsights } from "./observability/FounderInsights";
import { money, testKey } from "./observability/format";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LLMObservability() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    fetch(`${BASE}/api/admin/llm-usage?days=${days}`, { credentials: "include", signal: controller.signal })
      .then(async r => { if (!r.ok) throw new Error("Usage data is unavailable. Please retry."); return r.json(); })
      .then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(e => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [days, refresh]);

  const exportDaily = () => {
    const fields = ["date", "vendor", "cost_usd", "provider_reported_usd", "estimated_usd", "unknown_cost_requests", "calls", "errors", "total_tokens"];
    const csv = [fields.join(","), ...data.daily_vendor_rows.map(row => fields.map(f => JSON.stringify(row[f] ?? "")).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a"); link.href = url;
    link.download = `dnh-vendor-spend-${data.since.slice(0, 10)}-${data.through.slice(0, 10)}-UTC.csv`;
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const ready = data && data.period_days === days;
  return <section className="mx-auto max-w-[1280px] min-w-0 px-4 sm:px-6 lg:px-10 py-8 space-y-8" data-testid="llm-observability">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Usage period">
        {[1, 7, 30].map(d => <Button key={d} size="sm" variant={days === d ? "default" : "outline"}
          aria-pressed={days === d} onClick={() => setDays(d)} data-testid={`obs-period-${d}`}>
          {d === 1 ? "Today" : `${d} days`}
        </Button>)}
        <span className="text-xs text-[var(--muted)]" data-testid="obs-timezone">UTC</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={!ready || loading || !!error} onClick={exportDaily} data-testid="obs-export"><Download size={14} /> CSV</Button>
        <Button variant="ghost" size="icon" disabled={loading} title="Refresh usage" aria-label="Refresh usage" onClick={() => setRefresh(n => n + 1)} data-testid="obs-refresh"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></Button>
      </div>
    </div>
    {error && <p role="alert" className="text-sm text-[var(--crimson)]" data-testid="obs-error">{error}</p>}
    {loading && <p className="text-sm text-[var(--muted)]" data-testid="llm-obs-loading">Loading usage data…</p>}
    {ready && !error && <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 border-y border-[var(--hairline)] py-5">
        {[
          ["Recorded spend", money(data.total_cost_usd), `${money(data.provider_reported_usd)} reported · ${money(data.estimated_usd)} estimated`],
          ["Provider attempts", data.total_calls.toLocaleString(), `${data.failed_attempts} failed attempts`],
          ["Tokens", data.total_tokens.toLocaleString(), `${data.by_model.length} recorded models`],
          ["Unknown charges", data.unknown_cost_requests.toLocaleString(), "Requests excluded from spend"],
        ].map(([label, value, sub]) => <div key={label} className="min-w-0" data-testid={`obs-stat-${testKey(label)}`}>
          <div className="text-xs text-[var(--muted)]">{label}</div>
          <div className="font-serif text-2xl sm:text-3xl break-words py-1" data-testid={`obs-value-${testKey(label)}`}>{value}</div>
          <div className="text-xs text-[var(--muted)] break-words" data-testid={`obs-detail-${testKey(label)}`}>{sub}</div>
        </div>)}
      </div>
      <p className="text-xs leading-relaxed text-[var(--muted)]" data-testid="obs-cost-note">{data.cost_note}</p>
      {Object.entries(data.routing.unavailable_vendors || {}).map(([vendor, state]) => <p key={vendor} role="status" className="text-sm border-l-2 border-[var(--crimson)] pl-4 leading-relaxed" data-testid={`obs-provider-notice-${testKey(vendor)}`}>
        <strong>{vendor} direct access unavailable.</strong> {state.message}
      </p>)}
      <VendorSpend data={data} />
      <SpendChart data={data} />
      <UsageBreakdowns data={data} />
      <details className="border-y border-[var(--hairline)] py-4" data-testid="obs-routing-details">
        <summary className="cursor-pointer text-sm font-medium" data-testid="obs-routing-toggle">Active routing policy</summary>
        <div className="pt-4 space-y-3 text-sm" data-testid="obs-routing-policy">
          <p data-testid="obs-primary-route">Primary: {data.routing.primary}</p>
          {data.routing.specialists.map(s => <p key={s.task} className="break-words" data-testid={`obs-specialist-${testKey(s.task)}`}><strong>{s.task}:</strong> {s.vendor} · {s.model}{s.preferred_vendor !== s.vendor ? ` (${s.preferred_vendor} unavailable)` : ""}</p>)}
          <p data-testid="obs-last-resort">Last resort: {data.routing.last_resort}</p>
          <p className="text-xs text-[var(--muted)]" data-testid="obs-route-limits">Up to {data.routing.max_attempts} attempts · {data.routing.max_output_tokens} output tokens per attempt · No daily spending cap. {data.routing.failure_policy}</p>
        </div>
      </details>
      <FounderInsights days={days} />
    </>}
  </section>;
}